/**
 * ClassifyReportModal — 품목분류 검토보고서(미리보기 + PDF 다운로드 + 이메일).
 *   props: reportData(구조화 결과), onClose, reportId?(재발급 시 저장 report_id 재사용).
 *   서버(Spring /api/onto/report/classify/*)가 A4 보고서 HTML/PDF 생성.
 *   미리보기=iframe 격리(보고서 @page CSS 누출 방지). 이메일 수신자=본인 디폴트·편집 가능.
 *   발급(PDF/이메일) 성공 시 이력 자동 저장(보고서당 1건, report_id dedup) — [[reportHistoryApi]].
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { newReportId, saveReport } from "./reportHistoryApi";
import { summarizeForeignTariff } from "./ForeignTariffPanel";
import { attachAlternatives, blockingHs6, buildFtaOrigin, matchRequirement, parseExempt, REPORT_AGREEMENTS, COUNTRY_OPTIONS } from "./reportAnalysis";

function token() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}
function authHeaders(json) {
  const h = {};
  const t = token();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h["Content-Type"] = "application/json";
  return h;
}
// 로그인 사용자 이메일 추정(userId 가 이메일 형태면 사용).
function defaultEmail() {
  const uid = localStorage.getItem("userId") || sessionStorage.getItem("userId") || "";
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(uid)) return uid;
  return localStorage.getItem("userEmail") || "";
}

const BASE = "/api/onto/report/classify";

// 주요 FTA 코드→라벨(보고서 세율 요약용, ftaGroupMap 전체 대신 핵심만).
const MAJOR_FTA = [
  ["미국(한-미)", "FUS1"],
  ["EU(한-EU)", "FEU1"],
  ["중국(한-중)", "FCN1"],
  ["아세안(한-아세안)", "FAS1"],
  ["베트남(한-베)", "FVN1"],
  ["싱가포르(한-싱)", "FSG1"],
  ["RCEP 아세안", "FRCAS1"],
  ["RCEP 중국", "FRCCN1"],
  ["RCEP 일본", "FRCJP1"],
];
const pct = (v) => (v == null ? null : `${v}%`);
// hsk-detail rates 코드맵 → 보고서 rates{basic,wto,fta,best,as_of}.
//   best = 기본·WTO·주요 FTA 중 가장 낮은 세율. FTA 면 '원산지 요건 충족 시'를 붙인다(판정 아님).
//   종량세 등 숫자가 아닌 세율은 비교에서 뺀다.
//   enduse = 세번 상세 enduse_rate_codes(용도세율인 세율 종류) — 그 세율엔 '(용도세율)'을 붙이고 '가장 낮은 세율'은 용도세율이 아닌 것에서 고른다.
//   용도세율이 더 낮으면 best_enduse 로 따로 싣는다(용도 요건·수리 전 승인이 있어야 적용).
export function buildRates(rates, enduse = {}) {
  if (!rates || typeof rates !== "object") return null;
  const isUse = (code) => !!(enduse && code && enduse[code]);
  const tag = (code, v) => (v == null ? null : isUse(code) ? `${pct(v)} (용도세율)` : pct(v));
  const fta = {};
  const pool = [];
  const consider = (label, value, isFta, code) => {
    if (typeof value === "number" && Number.isFinite(value)) pool.push({ label, value, isFta, use: isUse(code) });
  };
  consider("기본세율", rates.A, false, "A");
  const wtoCode = rates.W1 != null ? "W1" : "W2";
  const wto = rates[wtoCode];
  consider("WTO 협정세율", wto, false, wtoCode);
  for (const [label, code] of MAJOR_FTA) {
    if (rates[code] != null) {
      fta[label] = tag(code, rates[code]);
      consider(`FTA ${label}`, rates[code], true, code);
    }
  }
  pool.sort((a, b) => a.value - b.value || Number(a.isFta) - Number(b.isFta));
  const low = pool.find((p) => !p.use);
  const lowUse = pool.find((p) => p.use);
  const out = {
    basic: tag("A", rates.A),
    wto: tag(wtoCode, wto),
    fta,
    ...(low ? { best: { rate: pct(low.value), label: low.isFta ? `${low.label} · 원산지 요건 충족 시` : low.label } } : {}),
    ...(lowUse && (!low || lowUse.value < low.value)
      ? { best_enduse: { rate: pct(lowUse.value), label: `${lowUse.label} · 용도세율 — 용도 요건과 수리 전 승인 필요` } }
      : {}),
    as_of: new Date().toISOString().slice(0, 10),
  };
  return out.basic || out.wto || Object.keys(fta).length ? out : null;
}

// hsk-detail → 보고서 compliance — 세관장확인(수입·현재 적용)·원산지표시·변경고시. 해당 여부 판정은 하지 않는다.
/**
 * 결정 세번의 호해설·류주(관세법령정보포털 원문) → 보고서 evidence 맨 앞 항목. 표제가 그 호·류인 것만.
 *   heading/chapter = notes-search items. 본문은 앞 400자.
 */
export function headingEvidence(hsk, heading, chapter) {
  const d = String(hsk || "").replace(/\D/g, "");
  if (d.length < 4) return [];
  const hs4 = d.slice(0, 4);
  const ch = String(Number(d.slice(0, 2)));
  const clip = (t) => {
    const s = String(t || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return s.length > 400 ? s.slice(0, 400) + "…" : s;
  };
  const pick = (items, re) => ((items || []).find((x) => re.test(String(x && x.label))) || null);
  const out = [];
  const h = pick(heading, new RegExp(`제\\s*${hs4}\\s*호`));
  if (h) out.push({ label: h.label, text: clip(h.desc) });
  const c = pick(chapter, new RegExp(`제\\s*0?${ch}\\s*류`));
  if (c) out.push({ label: c.label, text: clip(c.desc) });
  return out;
}

export function buildCompliance(detail, description = "") {
  if (!detail || typeof detail !== "object") return null;
  const checks = ((detail.customs_checks && detail.customs_checks.items) || [])
    .filter((it) => it && it.direction === "import" && it.currently_required !== false)
    .slice(0, 8)
    .map((it) => {
      const text = String(it.text || "").replace(/^\[[^\]]*\]\s*\n?\.?\s*/, "").trim();
      const m = matchRequirement(it.text, description);
      return {
        match: m.items
          ? (m.hits.length ? `품명과 겹치는 대상 항목: ${m.hits.map((h) => `${h.item}(${h.words.join("·")})`).join(", ")}` : `대상 항목 ${m.items}개 중 품명과 겹치는 말 없음`)
          : "",
        law: it.related_law || "",
        document: it.document_name || "",
        agencies: (it.agencies || []).map((a) => a && a.name).filter(Boolean).slice(0, 3).join(" · "),
        text: text.length > 400 ? text.slice(0, 400) + "…" : text,
      };
    });
  const om = detail.origin_marking || {};
  const method = ((om.methods || [])[0] || {}).method || "";
  const am = detail.amendment || {};
  const amendments = [...(am.moved_out || []), ...(am.moved_in || [])]
    .filter((a) => a && a.gosiNo)
    .slice(0, 5)
    .map((a) => ({ gosi_no: a.gosiNo, title: a.title || "" }));
  const out = {
    as_of: (detail.customs_checks && detail.customs_checks.as_of) || "",
    checks,
    origin_marking_target: om.target === "Y" ? "Y" : "",
    origin_marking_method: String(method).replace(/^[-\s]+/, ""),
    amendments,
    enduse: parseExempt(detail.refund_info && detail.refund_info.exempt_raw),
  };
  return checks.length || out.origin_marking_target || amendments.length || out.enduse.length ? out : null;
}

export default function ClassifyReportModal({ reportData, onClose, reportId: reportIdProp }) {
  const [data, setData] = useState(reportData);
  // 2·3단계(문의 #153): 세번 상세 세율·협정별 원산지결정기준을 들고 BOM 입력으로 다시 계산한다.
  const [origin, setOrigin] = useState(null); // { rates, psrs }
  const [bom, setBom] = useState([{ name: "", hs: "", country: "", amount: "" }]);
  const [price, setPrice] = useState("");
  const [bomOpen, setBomOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState(defaultEmail());
  const [toast, setToast] = useState(null);
  // 발급 세션 report_id — 재발급이면 저장 id 재사용(같은 이력 행 갱신), 아니면 새로 부여.
  const [reportId] = useState(() => reportIdProp || newReportId());

  // 발급 성공 시 이력 자동 저장(fire-and-forget). data=세율 병합된 스냅샷.
  const persist = (issuedVia, toEmail) =>
    saveReport({
      report_id: reportId,
      product_name: data.product_name,
      decided_hsk: data.decided_hsk,
      report: data,
      issued_via: issuedVia,
      to: toEmail,
    });

  // 결정세번(10자리)의 세율·통관요건 조회 → 보고서에 병합(세율·통관요건 섹션 채움·시점 고정).
  useEffect(() => {
    const digits = String(reportData.decided_hsk || "").replace(/\D/g, "");
    if ((reportData.rates && reportData.compliance) || digits.length < 10) return; // 이미 있거나 10자리 아니면 skip
    let alive = true;
    fetch(`/api/onto/hsk-detail?hsk=${digits.slice(0, 10)}&lang=ko`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        const rates = reportData.rates ? null : buildRates(d && d.rates, d && d.enduse_rate_codes);
        const compliance = reportData.compliance ? null : buildCompliance(d, reportData.product_name);
        const nature = !reportData.nature_class && d && Array.isArray(d.nature_class) && d.nature_class.length ? d.nature_class : null;
        if (alive && (rates || compliance || nature))
          setData((prev) => ({ ...prev, ...(rates ? { rates } : {}), ...(compliance ? { compliance } : {}), ...(nature ? { nature_class: nature } : {}) }));
      })
      .catch(() => {}); // 세율 실패해도 보고서는 생성(섹션만 생략)
    return () => {
      alive = false;
    };
  }, [reportData]);

  // 협정별 원산지결정기준(서버가 관세청 FTA 포털 자료를 저장·프록시) — 실패한 협정은 '기준 없음'으로 둔다.
  useEffect(() => {
    const digits = String(reportData.decided_hsk || "").replace(/\D/g, "");
    if (reportData.fta_origin || digits.length < 10) return;
    let alive = true;
    const hs6 = digits.slice(0, 6);
    Promise.all([
      fetch(`/api/onto/hsk-detail?hsk=${digits.slice(0, 10)}&lang=ko`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ...REPORT_AGREEMENTS.map((a) =>
        fetch(`/api/onto/fta-psr?ftaId=${a.ftaId}&hs6=${hs6}`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ),
    ]).then(([detail, ...ps]) => {
      if (!alive) return;
      const psrs = {};
      REPORT_AGREEMENTS.forEach((a, i) => { psrs[a.ftaId] = ps[i]; });
      const st = { rates: (detail && detail.rates) || {}, psrs, enduse: (detail && detail.enduse_rate_codes) || {} };
      setOrigin(st);
      const fo = buildFtaOrigin({ hsk: digits, rates: st.rates, psrs, bom: [], price: "", enduse: st.enduse });
      if (fo) setData((prev) => ({ ...prev, fta_origin: fo }));
    });
    return () => {
      alive = false;
    };
  }, [reportData]);

  // 관세청 법령해석 — 결정 세번·품명으로, 그리고 같은 세번의 FTA·원산지·용도세율 해석을 따로 찾아 합친다(최대 6건).
  useEffect(() => {
    const digits = String(reportData.decided_hsk || "").replace(/\D/g, "");
    if (reportData.interpretations || digits.length < 4) return;
    let alive = true;
    const call = (q, limit) =>
      fetch(`/api/onto/law-interpretations?hs=${digits.slice(0, 10)}&q=${encodeURIComponent(q)}&limit=${limit}`, { headers: authHeaders(false) })
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .then((d) => (d && d.items) || [])
        .catch(() => []);
    Promise.all([call(reportData.product_name || "", 4), call("FTA 원산지 용도세율 감면", 3)]).then(([a, b]) => {
      if (!alive) return;
      const seen = new Set();
      const list = [...a, ...b.filter((x) => x.matched_hs)]
        .filter((x) => !seen.has(x.serial_no) && seen.add(x.serial_no))
        .slice(0, 6)
        .map(({ serial_no, title, date, field, matched_hs, answer_gist }) => ({ serial_no, title, date, field, matched_hs, answer_gist }));
      if (list.length) setData((prev) => ({ ...prev, interpretations: list }));
    });
    return () => {
      alive = false;
    };
  }, [reportData]);

  // 상대국 관세(수출) — 관세청 국가별 관세율표 요약(공개 자료, 나라별 일반 세율 범위·한국 협정 최저).
  useEffect(() => {
    const digits = String(reportData.decided_hsk || "").replace(/\D/g, "");
    if (reportData.foreign_tariff || digits.length < 6) return;
    let alive = true;
    fetch(`/api/onto/foreign-tariff?hs6=${digits.slice(0, 6)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const rows = d ? summarizeForeignTariff(d, 20) : [];
        if (alive && rows.length) setData((prev) => ({ ...prev, foreign_tariff: { rows, total: (d.countries || []).length, source: d.source } }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [reportData]);

  // 결정 세번의 호해설·류주를 불러와 '분류 근거' 맨 앞에 — 검색으로 나온 해설은 다른 호일 수 있다(PREP 이유의 원천).
  useEffect(() => {
    const digits = String(reportData.decided_hsk || "").replace(/\D/g, "");
    if (reportData.heading_evidence_loaded || digits.length < 4) return;
    let alive = true;
    const call = (type) => {
      const u = new URL("/api/onto/notes-search", window.location.origin);
      u.searchParams.set("q", "");
      u.searchParams.set("hs4", digits.slice(0, 4));
      u.searchParams.set("lang", "kor");
      u.searchParams.set("limit", "5");
      u.searchParams.set("note_type", type);
      u.searchParams.set("full", "1");
      return fetch(u.toString()).then((r) => (r.ok ? r.json() : null)).then((d) => (d && d.items) || []).catch(() => []);
    };
    Promise.all([call("hsfdManl"), call("hstdManl")]).then(([h, c]) => {
      const add = headingEvidence(digits, h, c);
      if (!alive || !add.length) return;
      const labels = new Set(add.map((x) => x.label));
      setData((prev) => ({ ...prev, heading_evidence_loaded: true, evidence: [...add, ...(prev.evidence || []).filter((e) => !labels.has(e.label))] }));
    });
    return () => {
      alive = false;
    };
  }, [reportData]);

  const applyBom = () => {
    if (!origin) return;
    const fo = buildFtaOrigin({ hsk: data.decided_hsk, rates: origin.rates, psrs: origin.psrs, bom, price, enduse: origin.enduse });
    if (!fo) return;
    setData((prev) => ({ ...prev, fta_origin: fo }));
    // BOM 재료 표 — 재료마다 관세청 신성질별 분류(원자재·자본재·소비재). 분류를 못 받아도 표는 싣는다.
    const mats = bom.filter((m) => m && (m.name || m.hs || m.amount));
    const codes = [...new Set(mats.map((m) => String(m.hs || "").replace(/\D/g, "")).filter((h) => h.length >= 6))];
    const putBom = (cls) =>
      setData((prev) => ({
        ...prev,
        bom_table: {
          price: String(price || ""),
          rows: mats.map((m) => {
            const h = String(m.hs || "").replace(/\D/g, "");
            const path = (cls && cls[h]) || [];
            return { name: m.name || "", hs: h, country: m.country || "", amount: String(m.amount || ""), nature: path.slice(0, 2).join(" > ") };
          }),
        },
      }));
    putBom(null);
    if (codes.length)
      fetch(`/api/onto/nature-class?hs=${codes.join(",")}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => d && putBom(d.items || {}))
        .catch(() => {});
    // 공급망 대안 — 걸리는 재료마다 관세청 국가별 수입 실적(서버가 공공데이터포털 조회·7일 기억). 실패하면 대안 없이 둔다.
    const targets = blockingHs6(fo);
    if (!targets.length) return;
    Promise.all(
      targets.map((h) =>
        fetch(`/api/onto/trade-stats/imports?hs6=${h}`, { headers: authHeaders(false) })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      ),
    ).then((list) => {
      const stats = {};
      targets.forEach((h, i) => { stats[h] = list[i]; });
      setData((prev) => (prev.fta_origin === fo ? { ...prev, fta_origin: attachAlternatives(fo, stats) } : prev));
    });
  };
  const setRow = (i, k, v) => setBom((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  // 미리보기 로드 — data(세율 병합본) 변경 시 재생성
  useEffect(() => {
    let alive = true;
    setPreviewErr(null);
    setPreviewHtml(null);
    fetch(`${BASE}/preview`, {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify(data),
    })
      .then((r) => (r.ok ? r.text() : r.json().then((j) => Promise.reject(new Error(j.error || "HTTP " + r.status)))))
      .then((html) => alive && setPreviewHtml(html))
      .catch((e) => alive && setPreviewErr(String(e.message || e)));
    return () => {
      alive = false;
    };
  }, [data]);

  const flash = (m) => {
    setToast(m);
    setTimeout(() => setToast(null), 3500);
  };

  const download = () => {
    setBusy(true);
    fetch(`${BASE}/pdf`, {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify(data),
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("HTTP " + r.status))))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `검토보고서_${String(data.decided_hsk || "HS").replace(/\D/g, "")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        persist("pdf", null); // 다운로드 성공 → 이력 자동 저장
      })
      .catch((e) => flash("PDF 다운로드 실패: " + e.message))
      .finally(() => setBusy(false));
  };

  const sendEmail = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())) {
      flash("유효한 수신자 이메일을 입력하세요.");
      return;
    }
    setBusy(true);
    fetch(`${BASE}/email`, {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify({ ...data, to: to.trim() }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (ok && j.ok) {
          flash(`${to.trim()} 로 발송했습니다${j.attached ? " (PDF 첨부)" : " (본문)"}`);
          persist("email", to.trim()); // 발송 성공 → 이력 자동 저장
        } else flash("발송 실패: " + (j.message || j.error || "오류"));
      })
      .catch((e) => flash("발송 실패: " + e.message))
      .finally(() => setBusy(false));
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 1200,
        padding: "32px 20px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--audit-bg, #fff)",
          border: "1px solid var(--audit-border, #e5e7eb)",
          borderRadius: 10,
          width: "100%",
          maxWidth: 820,
          maxHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          color: "var(--audit-text, #111)",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid var(--audit-border, #e5e7eb)",
          }}
        >
          <b style={{ fontSize: 14 }}>품목분류 검토보고서</b>
          <button
            onClick={onClose}
            style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "var(--audit-text-muted)" }}
            title="닫기"
          >
            ✕
          </button>
        </div>

        {/* 원산지 판정(선택) — BOM 입력 */}
        {origin && (
          <div style={{ padding: "8px 16px", borderBottom: "1px solid var(--audit-border, #e5e7eb)", fontSize: 12.5 }}>
            <button type="button" className="audit-btn" onClick={() => setBomOpen((v) => !v)} aria-expanded={bomOpen}>
              {bomOpen ? "▾" : "▸"} FTA 원산지 판정 — BOM 입력(선택)
            </button>
            {bomOpen && (
              <div data-testid="bom-form" style={{ marginTop: 8 }}>
                <div style={{ color: "var(--audit-text-muted)", marginBottom: 6 }}>
                  재료마다 세번·원산지·금액을 넣으면 싱가포르·한-아세안·RCEP 원산지결정기준으로 충족 여부를 계산합니다. 금액과 제품 가격은 같은 통화로 넣으세요. 누적·미소기준·직접운송은 계산하지 않습니다.
                </div>
                <label style={{ display: "inline-flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                  제품 가격(FOB)
                  <input aria-label="제품 가격" value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: 120 }} />
                </label>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ color: "var(--audit-text-muted)", textAlign: "left" }}>
                      <th>재료명</th><th>세번(6자리 이상)</th><th>원산지</th><th>금액</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {bom.map((r, i) => (
                      <tr key={i}>
                        <td><input aria-label={`재료명 ${i + 1}`} value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} style={{ width: "100%" }} /></td>
                        <td><input aria-label={`세번 ${i + 1}`} value={r.hs} onChange={(e) => setRow(i, "hs", e.target.value)} style={{ width: 110 }} /></td>
                        <td>
                          <select aria-label={`원산지 ${i + 1}`} value={r.country} onChange={(e) => setRow(i, "country", e.target.value)}>
                            <option value="">선택</option>
                            {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td><input aria-label={`금액 ${i + 1}`} value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} style={{ width: 100 }} /></td>
                        <td>
                          {bom.length > 1 && (
                            <button type="button" className="audit-btn" onClick={() => setBom((rows) => rows.filter((_, j) => j !== i))} aria-label={`재료 ${i + 1} 지우기`}>✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button type="button" className="audit-btn" onClick={() => setBom((rows) => [...rows, { name: "", hs: "", country: "", amount: "" }])}>+ 재료 추가</button>
                  <button type="button" className="audit-btn audit-btn-primary" onClick={applyBom} style={{ background: "var(--audit-accent, #2563eb)", color: "#fff", borderColor: "var(--audit-accent, #2563eb)" }}>보고서에 판정 반영</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 미리보기 */}
        <div style={{ flex: 1, overflow: "auto", background: "#f3f4f6", padding: 12, minHeight: 200 }}>
          {previewErr ? (
            <div style={{ padding: 20, fontSize: 13, color: "#b91c1c" }}>
              미리보기 실패 — {previewErr}
            </div>
          ) : previewHtml == null ? (
            <div style={{ padding: 20, fontSize: 13, color: "var(--audit-text-muted)" }}>
              보고서 생성 중…
            </div>
          ) : (
            <iframe
              title="검토보고서 미리보기"
              srcDoc={previewHtml}
              style={{
                width: "100%",
                height: 520,
                border: "1px solid #ddd",
                borderRadius: 4,
                background: "#fff",
              }}
            />
          )}
        </div>

        {/* 액션바 */}
        <div
          style={{
            borderTop: "1px solid var(--audit-border, #e5e7eb)",
            padding: "10px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--audit-text-muted)", flexShrink: 0 }}>이메일</span>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="수신자 이메일 (본인 기본, 변경 가능)"
              style={{
                flex: 1,
                minWidth: 180,
                padding: "6px 10px",
                fontSize: 13,
                border: "1px solid var(--audit-border-strong, #cdd1d8)",
                borderRadius: 6,
                background: "var(--audit-bg, #fff)",
                color: "var(--audit-text, #111)",
                fontFamily: "inherit",
              }}
            />
            <button
              onClick={sendEmail}
              disabled={busy || !previewHtml}
              className="audit-btn audit-btn-primary"
              style={{ opacity: busy || !previewHtml ? 0.6 : 1, whiteSpace: "nowrap" }}
            >
              이메일 전송
            </button>
            <button
              onClick={download}
              disabled={busy || !previewHtml}
              className="audit-btn"
              style={{ opacity: busy || !previewHtml ? 0.6 : 1, whiteSpace: "nowrap" }}
            >
              PDF 다운로드
            </button>
          </div>
          {toast && (
            <div style={{ fontSize: 12.5, color: "var(--audit-accent, #2563eb)" }}>{toast}</div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
